import { useState } from "react";
import FileUpload from "../../../../../../components/elements/FileUpload/FileUpload";
import useImage from "../../../../../../hooks/imageHook";
import { CLAIM_STAGES, UPLOAD_IMAGE_ENDPOINTS, UPLOADABLE_DOCUMENT_TYPES } from "../../../../../../resources/enums";
import Button from "../../../../../../components/form-control/Button";
import SelectDropdown, { ISelectDropdownItem } from "../../../../../../components/form-control/SelectDropdown";
import { showToast } from "../../../../../../utils/miscFunctions";

interface UploadClaimDocumentProps {
    claimNumber: string;
    onProceed: () => void;
    onClose?: () => void;
    url: UPLOAD_IMAGE_ENDPOINTS;
    queryKey?: string;
    stage?: CLAIM_STAGES;
}

const getDocumentsByStage = (stage: CLAIM_STAGES | undefined): ISelectDropdownItem[] => {
    switch (stage) {
        case CLAIM_STAGES.NEW:
        case CLAIM_STAGES.IN_REVIEW:
            return [
                {
                    name: 'Claim Form',
                    value: 'CLAIM_FORM'
                },
                {
                    name: 'Claim Estimate',
                    value: 'CLAIM_ESTIMATE'
                }
            ];
        
        case CLAIM_STAGES.SUBMITTED:
            return [
                {
                    name: 'Claim Form',
                    value: 'CLAIM_FORM'
                },
                {
                    name: 'Claim Estimate',
                    value: 'CLAIM_ESTIMATE'
                }
            ];
        
        case CLAIM_STAGES.ACKNOWLEDGED:
            return [
                {
                    name: 'Claim Form',
                    value: 'CLAIM_FORM'
                },
                {
                    name: 'Claim Estimate',
                    value: 'CLAIM_ESTIMATE'
                }
            ];
        
        case CLAIM_STAGES.ASSESSED:
            return [
                {
                    name: 'Claim Form',
                    value: 'CLAIM_FORM'
                },
                {
                    name: 'Claim Estimate',
                    value: 'CLAIM_ESTIMATE'
                }
            ];
        
        case CLAIM_STAGES.ADJUSTED:
            return [
                {
                    name: 'Claim Form',
                    value: 'CLAIM_FORM'
                },
                {
                    name: 'Claim Estimate',
                    value: 'CLAIM_ESTIMATE'
                },
                {
                    name: 'DV',
                    value: 'DV'
                },
                {
                    name: 'Cover Letter',
                    value: 'COVER_LETTER'
                }
            ];
        
        case CLAIM_STAGES.DV_ISSUED:
            return [
                {
                    name: 'DV',
                    value: 'DV'
                },
                {
                    name: 'Cover Letter',
                    value: 'COVER_LETTER'
                }
            ];
        
        case CLAIM_STAGES.DV_SIGNED:
            return [
                {
                    name: 'DV',
                    value: 'DV'
                },
                {
                    name: 'Cover Letter',
                    value: 'COVER_LETTER'
                }
            ];
        
        case CLAIM_STAGES.PAID:
            return [
                {
                    name: 'Payment Advice',
                    value: 'PAYMENT_ADVICE'
                }
            ];
        
        default:
            return [
                {
                    name: 'Claim Form',
                    value: 'CLAIM_FORM'
                },
                {
                    name: 'Claim Estimate',
                    value: 'CLAIM_ESTIMATE'
                }
            ];
    }
};

const UploadClaimDocument = ({ claimNumber, onProceed, onClose, url, queryKey , stage}: UploadClaimDocumentProps) => {
    const [loading, setLoading] = useState<boolean>(false);
    const [claimDocument, setClaimDocument] = useState<File | null>(null);
    const { uploadClaimDocument } = useImage()
    const [selectedDocumentType, setSelectedDocumentType] = useState<UPLOADABLE_DOCUMENT_TYPES>();
    
    const availableDocuments = getDocumentsByStage(stage);

    const handleUpload = async (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        try {
            if (claimDocument && selectedDocumentType) {
                setLoading(true);
                const response: any = await uploadClaimDocument(claimDocument, selectedDocumentType, url, claimNumber)
                if (response) {
                    showToast.success(response?.message || 'Document uploaded successfully');
                    onProceed()
                    onClose?.()
                }
            }
        } catch (error: any) {
            showToast.error(error.description ?? 'Failed to upload document');
        } finally {
            setLoading(true);
        }
    };

    return (
        <div className="p-5  w-full md:w-full">
            <form onSubmit={handleUpload}>
                <div className="mb-4">
                    <SelectDropdown
                        name="doc"
                        label="Select document to upload"
                        listSelectedValue={selectedDocumentType}
                        onChange={(value: any) => {
                            setSelectedDocumentType(value);
                        }}
                        search={true}
                        list={availableDocuments || []}
                        required
                    />
                </div>

                {selectedDocumentType && (
                    <FileUpload
                        onFileLoad={(doc: any) => {
                            if (doc && doc[0]) {
                                setClaimDocument(doc[0]);
                            }
                        }}
                        label=""
                        maxHeight={350}
                        maxWidth={300}
                        imageContainerWidth="100%"
                        cropImage={false}
                        onFileDeleted={() => { }}
                        showFileMax={false}
                        savedImage={null}
                    />
                )}

                <div className="w-full flex gap-4 mt-6 !mb-4">
                    <Button
                        type="button"
                        label="Cancel"
                        onClick={onClose}
                        fullWidth
                        bgColor='secondary'
                    />

                    <Button
                        type="submit"
                        label="Save"
                        fullWidth
                        loading={loading}
                        disabled={selectedDocumentType && claimDocument ? false : true}
                        bgColor='primary'
                    />
                </div>
            </form>
        </div>
    );
};

export default UploadClaimDocument;
